import DefaultLayout from '../../components/DefaultLayout';
import PublicRoute from 'enums/publicRoutes';
import { Form } from '../../../ui/Form/Form';
import { DefaultThirdHeader } from '../../components/DefaultLayout/DefaultThirdHeader';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { Button, Icon, Spinner } from '../../../ui';
import { Card } from '../../../ui/Card';
import { ProductData } from '../../../types/ProductData';
import { useCallback, useEffect, useState } from 'react';
import { ProductsTopFilter } from '../../../ui/Form/filters/ProductsTopFilter';
import { options } from '../../../config/config';
import { StyledAllNewProducts } from './style';
import { Filter } from 'types/Filter';
import { FilterOptions } from 'types/FilterOptions';
import {
  useBannerState,
  useCategoryState,
  useProductState,
} from 'public/context';
import PopularCategories from 'public/components/DefaultLayout/PopularCategories';
import Banner from 'public/components/DefaultLayout/Banner';
import { MobileMenuDrawer } from 'ui/Drawer/Drawers/MobileMenuDrawer';
import { StyledMobileFilterFooter } from 'public/components/MobileLayout/MobileMenu/MobileFilterFooter/style';
import MobileFilter from 'public/components/MobileLayout/MobileFilter/MobileFilter';
import MobileProductCategoriesCarousel from 'public/components/MobileLayout/MobileProductCategoriesCarousel/MobileProductCategoriesCarousel';
import SearchAndFilter from 'public/components/DefaultLayout/SearchAndFilter';
import {
  useFilterDispatch,
  useFilterState,
} from 'public/context/FilterContext';
import DiscountItems from 'public/components/DefaultLayout/DiscountItems/DiscountItems';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCategoryDispatch } from '../../../public/context';
import DefaultPagination from '../../components/DefaultLayout/DefaultPagination';
import useSearchParamsHelper from '../../../utils/useSearchParamsHelper';
import useScroll from 'utils/useScroll';
import {
  buildMapNavigationParams,
  buildFilterChangeParams,
} from '../../../utils/filterHelper';
import { useListPageUrlSync } from '../../../utils/useListPageUrlSync';
import { buildPath, useListPagination } from '../../../utils/listPagination';
import useMarketType from '../../../utils/useMarketType';
import { useClearFilterHandlers } from '../../../utils/useClearFilterHandlers';

const DEFAULT_IMAGE = options.images.defaultProductImage;

export const AllNewProducts = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigation = useNavigate();
  const [filter, setFilter] = useState<Filter>({});
  const [total, setTotal] = useState<number>(0);
  const [products, setProducts] = useState<ProductData[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});

  const { popularCategory, activeCategory } = useCategoryState();
  const { productsDiscounted } = useProductState();
  const { banners } = useBannerState();

  const dispatchFilter = useFilterDispatch();
  const { isOpenMobileFilter } = useFilterState();
  const { getProperFilterValues } = useSearchParamsHelper();
  const [prevSearch, setPrevSearch] = useState<string>('');
  const { scrollToTop } = useScroll();

  const {
    page: currentPage,
    pageSize,
    setPage,
    setPageSize,
  } = useListPagination();
  const { marketType, isEmarket } = useMarketType();

  const openCloseDrawer = useCallback(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isOpenMobileFilter: !isOpenMobileFilter },
    });
    addClassToHeader();
  }, [isOpenMobileFilter]);

  useEffect(() => {
    dispatchFilter({
      type: 'SAVE_PAYLOAD',
      payload: { isFilter: true, openCloseDrawer },
    });
  }, [dispatchFilter, openCloseDrawer]);

  const addClassToHeader = () => {
    const header = document.getElementById('mobile-header-container');
    if (header) {
      if (!isOpenMobileFilter) {
        header.classList.add('filterOpen');
      } else {
        header.classList.remove('filterOpen');
      }
    }
  };

  const banner_3_data = banners.data.find(
    (banner) => banner.type === 'banner_3'
  );
  const banner_4_data = banners.data.find(
    (banner) => banner.type === 'banner_4'
  );

  const { isLoading, appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/products',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setTotal(response.products.total);
      setProducts(response.products.data);
      setFilterOptions(response.filters);
    },
  });

  useListPageUrlSync({
    setFilter,
    setPrevSearch,
    prevSearch,
    scrollToTop,
    getProperFilterValues,
    filterFields: ['qmark', 'delivery', 'services', 'farm', 'cooperative'],
    onSync: ({ filterParams, page, page_size, currentSearch }) => {
      appendData({
        filter: { ...filterParams, type: marketType },
        page,
        page_size,
        options: { view: 'new' },
        fullSearch: currentSearch,
      });
    },
  });

  const handleFilterChange = (newFilter: Filter) => {
    const oldParams = new URLSearchParams(location.search);
    const params = buildFilterChangeParams({
      filter: newFilter,
      search: oldParams.get('search') || undefined,
      pageSize,
    });
    navigation(buildPath(location.pathname, params), {
      replace: true,
    });
  };

  const { clearAppliedFilters, clearDraftFilters } = useClearFilterHandlers({
    filter,
    setFilter,
    onFilterChange: handleFilterChange,
    preserveOrdering: true,
  });

  const drawer = () => {
    return (
      <MobileMenuDrawer
        title={intl.formatMessage({ id: 'product.filter' })}
        onClose={openCloseDrawer}
        open={isOpenMobileFilter}
        className="mobile-filter product-mobile-filter"
        closable={false}
        footer={
          <StyledMobileFilterFooter>
            <Button
              label={intl.formatMessage({ id: 'general.choose' })}
              icon={<Icon name={'mobile-filter-submit-arrow-icon'} />}
              onClick={() => handleFilterChange(filter)}
            />
          </StyledMobileFilterFooter>
        }
      >
        <MobileFilter
          setFilter={setFilter}
          filter={filter}
          filterOptions={filterOptions}
          onClearFilters={clearDraftFilters}
        />
      </MobileMenuDrawer>
    );
  };

  const dispatch = useCategoryDispatch();

  useEffect(() => {
    dispatch({
      type: 'SAVE_ACTIVE_CATEGORY',
      activeCategory: null,
    });
  }, []);

  const breadcrumbs = [
    {
      route: '/',
      name: intl.formatMessage({ id: 'general.start' }),
    },
    {
      route: 'new',
      name: intl.formatMessage({ id: 'general.news' }),
    },
  ];

  const handleShowMap = () => {
    const params = buildMapNavigationParams({
      dataType: 'products',
      page: currentPage,
      pageSize,
      filter,
      search: new URLSearchParams(location.search).get('search') || undefined,
    });
    navigation(`${PublicRoute.MAP}?${params.toString()}`, {
      state: { returnPath: PublicRoute.PRODUCTS_ALL_NEW },
    });
  };

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent className={'grey-background'}>
        <StyledAllNewProducts>
          <DefaultThirdHeader
            label={intl.formatMessage({ id: 'form.new_page_title' })}
            filters={
              <ProductsTopFilter
                total={total}
                per_page={pageSize}
                setFilter={handleFilterChange}
                page_size_options={options.filters.pageSizeOptions}
                setPageSize={setPageSize}
                filters={filter}
                unit="products"
                handleShowMap={handleShowMap}
              />
            }
            breadcrumb={breadcrumbs}
          />

          <SearchAndFilter rootClassName="laptop-search-section-box" />

          <div className="products-inner">
            <div className="container products-container">
              <div className="products-filters-container">
                <Form
                  setFilter={handleFilterChange}
                  filterOptions={filterOptions}
                  filter={filter}
                  onClearFilters={clearAppliedFilters}
                />
                <Spinner spinning={isLoading}>
                  <div className="products-wrapper">
                    <p className="category_name">
                      {activeCategory
                        ? activeCategory.name
                        : intl.formatMessage({ id: 'form.new_page_title' })}
                    </p>
                    <div className="products">
                      {products.map((product: ProductData, index) => (
                        <Card
                          key={`crd-${index}`}
                          product={product}
                          imageUrl={
                            product?.image_small
                              ? product?.image_small
                              : product?.farm?.logo
                                ? product?.farm?.logo
                                : DEFAULT_IMAGE
                          }
                          hoverable
                        />
                      ))}
                    </div>
                    {total > pageSize && (
                      <DefaultPagination
                        totalRecords={total}
                        pageSize={pageSize}
                        currentPage={currentPage}
                        onChange={(page) => setPage(page)}
                      />
                    )}
                  </div>
                </Spinner>
              </div>
            </div>
          </div>

          <MobileProductCategoriesCarousel popularCategory={popularCategory} />
          {isEmarket && <PopularCategories categories={popularCategory} />}

          <div className="discount-items">
            <DiscountItems products={productsDiscounted} slidesPerRow={1} />
          </div>

          <div className="banners">
            <div className="container">
              {banner_3_data && banner_4_data && (
                <Banner
                  banners={[banner_3_data, banner_4_data]}
                  className="desktop-banner"
                />
              )}
            </div>
          </div>
        </StyledAllNewProducts>

        {drawer()}
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};
